import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { PageTransition } from '@/components/PageTransition';
import { useSpaceData } from '@/hooks/useSpaceData';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { supabase } from '@/lib/supabase';
import {
  Play,
  Eye,
  MoreHorizontal,
  Clock,
  CheckCircle2,
  Circle,
  Trash2,
  ListChecks,
  GitBranch,
  XCircle,
  Calendar,
  Webhook,
  BarChart3,
  Filter,
  Users,
  Download,
  FileText,
  X,
  CalendarRange,
} from 'lucide-react';
import { format, formatDistanceToNow, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

export default function WorkflowReportsPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { userProfile, company } = useAuth();
  // Pass company.id to validate the space belongs to the user's current company
  const { space, spaceTools, loading, accessDenied } = useSpaceData(projectId, userProfile?.id, company?.id);

  // Set page title
  usePageTitle('Workflow Reports');

  // Redirect if user doesn't have access (e.g., switched companies)
  useEffect(() => {
    if (accessDenied) {
      toast.error('You no longer have access to this space');
      navigate('/dashboard', { replace: true });
    }
  }, [accessDenied, navigate]);

  const [workflows, setWorkflows] = useState([]);
  const [workflowRuns, setWorkflowRuns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState('all');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [isExporting, setIsExporting] = useState(false);
  const [deleteRunId, setDeleteRunId] = useState(null);

  // Load all workflows and runs for this space with single RPC call
  useEffect(() => {
    const loadData = async () => {
      if (!projectId) return;

      try {
        setIsLoading(true);

        // Single RPC call replaces 2 sequential queries
        const { data, error } = await supabase.rpc('get_workflow_reports_data', {
          p_project_id: projectId
        });

        if (error) throw error;

        setWorkflows(data?.workflows || []);
        setWorkflowRuns(data?.workflow_runs || []);
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load workflow data');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [projectId]);

  // Real-time subscription for workflow runs
  useEffect(() => {
    if (!projectId) return;

    let channel = null;
    const instanceId = Date.now();

    const setupSubscription = async () => {
      // Set auth token for Realtime (required for RLS policies)
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        await supabase.realtime.setAuth(session.access_token);
      }

      channel = supabase
        .channel(`workflow-reports-${projectId}-${instanceId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'checklists_workflow_runs',
            filter: `project_id=eq.${projectId}`,
          },
          async (payload) => {
            console.log('Workflow run change:', payload.eventType, payload);

            if (payload.eventType === 'INSERT') {
              // Fetch the full run data with relations
              const { data: newRun } = await supabase
                .from('checklists_workflow_runs')
                .select(`
                  *,
                  workflow:workflows!inner(id, name),
                  started_by_user:user_profiles!checklists_workflow_runs_started_by_fkey(id, first_name, last_name, avatar_url),
                  task_runs:checklists_workflow_task_runs(id, status, due_date)
                `)
                .eq('id', payload.new.id)
                .single();

              if (newRun) {
                setWorkflowRuns((prev) => [newRun, ...prev]);
              }
            } else if (payload.eventType === 'UPDATE') {
              // Update the run in the list
              const { data: updatedRun } = await supabase
                .from('checklists_workflow_runs')
                .select(`
                  *,
                  workflow:workflows!inner(id, name),
                  started_by_user:user_profiles!checklists_workflow_runs_started_by_fkey(id, first_name, last_name, avatar_url),
                  task_runs:checklists_workflow_task_runs(id, status, due_date)
                `)
                .eq('id', payload.new.id)
                .single();

              if (updatedRun) {
                setWorkflowRuns((prev) =>
                  prev.map((run) => (run.id === updatedRun.id ? updatedRun : run))
                );
              }
            } else if (payload.eventType === 'DELETE') {
              setWorkflowRuns((prev) =>
                prev.filter((run) => run.id !== payload.old.id)
              );
            }
          }
        )
        .subscribe((status, err) => {
          console.log('Workflow reports subscription status:', status);
          if (err) {
            console.error('Subscription error:', err);
          }
        });
    };

    setupSubscription();

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [projectId]);

  // Calculate stats across all workflows
  const stats = useMemo(() => {
    const activeRuns = workflowRuns.filter(r => r.status === 'in_progress').length;
    const completedRuns = workflowRuns.filter(r => r.status === 'completed').length;
    const totalTasks = workflows.reduce((sum, w) => sum + (w.tasks?.length || 0), 0);

    // Count unique assignees across all active runs
    const assigneeIds = new Set();
    workflowRuns.forEach(run => {
      if (run.status === 'in_progress') {
        run.task_runs?.forEach(tr => {
          tr.assignees?.forEach(a => {
            if (a.id) assigneeIds.add(a.id);
          });
        });
      }
    });

    return {
      activeRuns,
      completedRuns,
      totalWorkflows: workflows.length,
      totalRuns: workflowRuns.length,
      totalTasks,
      activeAssignees: assigneeIds.size,
    };
  }, [workflows, workflowRuns]);

  // Filter runs based on toggles, workflow selection, and date range
  const filteredRuns = useMemo(() => {
    let runs = workflowRuns;

    // Filter by workflow if not "all"
    if (selectedWorkflow !== 'all') {
      runs = runs.filter(r => r.workflow_id === selectedWorkflow);
    }

    // Filter by completed status
    if (!showCompleted) {
      runs = runs.filter(r => r.status !== 'completed');
    }

    // Filter by date range
    if (dateRange.from || dateRange.to) {
      runs = runs.filter(r => {
        if (!r.started_at && !r.created_date) return false;
        const runDate = new Date(r.started_at || r.created_date);

        if (dateRange.from && dateRange.to) {
          return isWithinInterval(runDate, {
            start: startOfDay(dateRange.from),
            end: endOfDay(dateRange.to),
          });
        } else if (dateRange.from) {
          return runDate >= startOfDay(dateRange.from);
        } else if (dateRange.to) {
          return runDate <= endOfDay(dateRange.to);
        }
        return true;
      });
    }

    return runs;
  }, [workflowRuns, selectedWorkflow, showCompleted, dateRange]);

  // Count completed for display
  const completedCount = useMemo(() => {
    if (selectedWorkflow === 'all') {
      return workflowRuns.filter(r => r.status === 'completed').length;
    }
    return workflowRuns.filter(r => r.workflow_id === selectedWorkflow && r.status === 'completed').length;
  }, [workflowRuns, selectedWorkflow]);

  // Pre-compute progress, due dates, and overdue status for all runs (O(n) once instead of O(n) per render per row)
  const runMetrics = useMemo(() => {
    const metrics = new Map();
    const now = new Date();

    workflowRuns.forEach(run => {
      const taskRuns = run.task_runs || [];

      // Calculate progress
      const completed = taskRuns.filter(t => t.status === 'completed').length;
      const total = taskRuns.length;
      const progress = { completed, total, percent: total > 0 ? (completed / total) * 100 : 0 };

      // Calculate due date (earliest incomplete task due date)
      const dueDates = taskRuns
        .filter(t => t.due_date && t.status !== 'completed')
        .map(t => new Date(t.due_date));
      const dueDate = dueDates.length > 0 ? new Date(Math.min(...dueDates)) : null;

      // Calculate overdue status
      const isOverdue = dueDate ? dueDate < now : false;

      metrics.set(run.id, { progress, dueDate, isOverdue });
    });

    return metrics;
  }, [workflowRuns]);

  // Helper to get metrics for a run (O(1) lookup)
  const getRunMetrics = (runId) => {
    return runMetrics.get(runId) || {
      progress: { completed: 0, total: 0, percent: 0 },
      dueDate: null,
      isOverdue: false
    };
  };

  // Export to CSV
  const exportToCSV = () => {
    if (filteredRuns.length === 0) {
      toast.error('No data to export');
      return;
    }

    setIsExporting(true);

    try {
      // CSV headers
      const headers = [
        'Workflow',
        'Run Name',
        'Status',
        'Started By',
        'Started At',
        'Completed At',
        'Tasks Completed',
        'Total Tasks',
        'Progress %',
        'Due Date',
      ];

      // CSV rows - use pre-computed metrics for O(1) lookup
      const rows = filteredRuns.map(run => {
        const { progress, dueDate } = getRunMetrics(run.id);
        const startedBy = run.trigger_type === 'webhook' || (!run.started_by && !run.started_by_user)
          ? 'Webhook'
          : run.started_by_user
            ? `${run.started_by_user.first_name || ''} ${run.started_by_user.last_name || ''}`.trim()
            : 'Unknown';

        return [
          run.workflow?.name || 'Unknown Workflow',
          run.run_name || 'Untitled Run',
          run.status,
          startedBy,
          run.started_at ? format(new Date(run.started_at), 'yyyy-MM-dd HH:mm') : '',
          run.completed_at ? format(new Date(run.completed_at), 'yyyy-MM-dd HH:mm') : '',
          progress.completed,
          progress.total,
          Math.round(progress.percent),
          dueDate ? format(dueDate, 'yyyy-MM-dd') : '',
        ];
      });

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
      ].join('\n');

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `workflow-reports-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  // Export to PDF
  const exportToPDF = async () => {
    if (filteredRuns.length === 0) {
      toast.error('No data to export');
      return;
    }

    setIsExporting(true);

    try {
      // Create a printable HTML document
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Please allow popups to export PDF');
        setIsExporting(false);
        return;
      }

      const dateRangeText = dateRange.from || dateRange.to
        ? `${dateRange.from ? format(dateRange.from, 'MMM d, yyyy') : 'Start'} - ${dateRange.to ? format(dateRange.to, 'MMM d, yyyy') : 'Present'}`
        : 'All Time';

      const workflowName = selectedWorkflow === 'all'
        ? 'All Workflows'
        : workflows.find(w => w.id === selectedWorkflow)?.name || 'Unknown';

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Workflow Reports - ${space?.name || 'Space'}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; }
            h1 { font-size: 24px; margin-bottom: 8px; }
            .subtitle { color: #666; margin-bottom: 20px; }
            .meta { display: flex; gap: 20px; margin-bottom: 20px; font-size: 14px; color: #666; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { padding: 8px 12px; text-align: left; border-bottom: 1px solid #eee; }
            th { background: #f5f5f5; font-weight: 600; }
            .status-completed { color: #16a34a; }
            .status-in_progress { color: #2563eb; }
            .status-cancelled { color: #6b7280; }
            .progress-bar { width: 100px; height: 8px; background: #eee; border-radius: 4px; overflow: hidden; }
            .progress-fill { height: 100%; background: #3b82f6; }
            .overdue { color: #dc2626; }
            @media print {
              body { padding: 0; }
              button { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>Workflow Reports</h1>
          <p class="subtitle">${space?.name || 'Space'}</p>
          <div class="meta">
            <span><strong>Workflow:</strong> ${workflowName}</span>
            <span><strong>Date Range:</strong> ${dateRangeText}</span>
            <span><strong>Total Runs:</strong> ${filteredRuns.length}</span>
            <span><strong>Generated:</strong> ${format(new Date(), 'MMM d, yyyy HH:mm')}</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Workflow</th>
                <th>Run Name</th>
                <th>Status</th>
                <th>Started By</th>
                <th>Started</th>
                <th>Progress</th>
                <th>Due Date</th>
              </tr>
            </thead>
            <tbody>
              ${filteredRuns.map(run => {
                // Use pre-computed metrics for O(1) lookup
                const { progress, dueDate, isOverdue } = getRunMetrics(run.id);
                const startedBy = run.trigger_type === 'webhook' || (!run.started_by && !run.started_by_user)
                  ? 'Webhook'
                  : run.started_by_user
                    ? `${run.started_by_user.first_name || ''} ${run.started_by_user.last_name || ''}`.trim()
                    : 'Unknown';

                return `
                  <tr>
                    <td>${run.workflow?.name || 'Unknown'}</td>
                    <td>${run.run_name || 'Untitled Run'}</td>
                    <td class="status-${run.status}">${run.status.replace('_', ' ')}</td>
                    <td>${startedBy}</td>
                    <td>${run.started_at ? format(new Date(run.started_at), 'MMM d, yyyy') : '-'}</td>
                    <td>
                      <div style="display: flex; align-items: center; gap: 8px;">
                        <div class="progress-bar">
                          <div class="progress-fill" style="width: ${progress.percent}%"></div>
                        </div>
                        <span>${progress.completed}/${progress.total}</span>
                      </div>
                    </td>
                    <td class="${isOverdue ? 'overdue' : ''}">${dueDate ? format(dueDate, 'MMM d, yyyy') : '-'}${isOverdue ? ' (Overdue)' : ''}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
        </html>
      `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();

      toast.success('PDF ready to print/save');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    } finally {
      setIsExporting(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'in_progress':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200"><Clock className="w-3 h-3 mr-1" /> In Progress</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" /> Completed</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200"><XCircle className="w-3 h-3 mr-1" /> Cancelled</Badge>;
      default:
        return <Badge variant="outline"><Circle className="w-3 h-3 mr-1" /> {status}</Badge>;
    }
  };

  if (loading || isLoading) {
    return (
      <AppShell3 projectId={projectId} activeTool="tasks">
        <PageTransition>
          <div className="min-h-screen flex items-center justify-center">
            <LoadingSpinner />
          </div>
        </PageTransition>
      </AppShell3>
    );
  }

  return (
    <AppShell3 projectId={projectId} activeTool="tasks">
      <ToolHeader
        spaceName={space?.name || 'Space'}
        spaceImage={space?.logo_url}
        toolName="Workflow Reports"
        currentTool="tasks"
        spaceTools={spaceTools}
        parentBreadcrumb={{
          name: 'Tasks',
          onClick: () => navigate(`/space/${projectId}/tasks`)
        }}
      />
      <PageTransition>
        <div className="p-6 space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="bg-card border rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{stats.totalWorkflows}</p>
                  <p className="text-sm text-muted-foreground">Workflows</p>
                </div>
              </div>
            </div>
            <div className="bg-card border rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{stats.activeRuns}</p>
                  <p className="text-sm text-muted-foreground">Active Runs</p>
                </div>
              </div>
            </div>
            <div className="bg-card border rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{stats.completedRuns}</p>
                  <p className="text-sm text-muted-foreground">Completed</p>
                </div>
              </div>
            </div>
            <div className="bg-card border rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <GitBranch className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{stats.totalRuns}</p>
                  <p className="text-sm text-muted-foreground">Total Runs</p>
                </div>
              </div>
            </div>
            <div className="bg-card border rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <ListChecks className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{stats.totalTasks}</p>
                  <p className="text-sm text-muted-foreground">Total Tasks</p>
                </div>
              </div>
            </div>
            <div className="bg-card border rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-pink-100 rounded-lg">
                  <Users className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{stats.activeAssignees}</p>
                  <p className="text-sm text-muted-foreground">Active Assignees</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Table */}
          <div>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
              <h2 className="text-lg font-semibold">All Workflow Runs</h2>
              <div className="flex items-center gap-4 flex-wrap">
                {/* Workflow Filter */}
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-muted-foreground" />
                  <Select value={selectedWorkflow} onValueChange={setSelectedWorkflow}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="All Workflows" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Workflows</SelectItem>
                      {workflows.map((workflow) => (
                        <SelectItem key={workflow.id} value={workflow.id}>
                          {workflow.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Range Filter */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`justify-start text-left font-normal ${
                        dateRange.from || dateRange.to ? '' : 'text-muted-foreground'
                      }`}
                    >
                      <CalendarRange className="mr-2 h-4 w-4" />
                      {dateRange.from ? (
                        dateRange.to ? (
                          <>
                            {format(dateRange.from, 'MMM d')} - {format(dateRange.to, 'MMM d, yyyy')}
                          </>
                        ) : (
                          <>From {format(dateRange.from, 'MMM d, yyyy')}</>
                        )
                      ) : dateRange.to ? (
                        <>Until {format(dateRange.to, 'MMM d, yyyy')}</>
                      ) : (
                        'Date range'
                      )}
                      {(dateRange.from || dateRange.to) && (
                        <X
                          className="ml-2 h-4 w-4 hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDateRange({ from: null, to: null });
                          }}
                        />
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <CalendarComponent
                      initialFocus
                      mode="range"
                      defaultMonth={dateRange.from || new Date()}
                      selected={dateRange}
                      onSelect={(range) => setDateRange(range || { from: null, to: null })}
                      numberOfMonths={2}
                    />
                    <div className="p-3 border-t flex justify-between">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDateRange({ from: null, to: null })}
                      >
                        Clear
                      </Button>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const today = new Date();
                            const lastWeek = new Date(today);
                            lastWeek.setDate(lastWeek.getDate() - 7);
                            setDateRange({ from: lastWeek, to: today });
                          }}
                        >
                          Last 7 days
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const today = new Date();
                            const lastMonth = new Date(today);
                            lastMonth.setDate(lastMonth.getDate() - 30);
                            setDateRange({ from: lastMonth, to: today });
                          }}
                        >
                          Last 30 days
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Show Completed Toggle */}
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-completed"
                    checked={showCompleted}
                    onCheckedChange={setShowCompleted}
                  />
                  <Label htmlFor="show-completed" className="text-sm text-muted-foreground cursor-pointer">
                    Show completed ({completedCount})
                  </Label>
                </div>

                {/* Export Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" disabled={isExporting || filteredRuns.length === 0}>
                      <Download className="w-4 h-4 mr-2" />
                      {isExporting ? 'Exporting...' : 'Export'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={exportToCSV}>
                      <FileText className="w-4 h-4 mr-2" />
                      Export as CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToPDF}>
                      <FileText className="w-4 h-4 mr-2" />
                      Export as PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {workflowRuns.length === 0 ? (
              <div className="border rounded-lg p-12 text-center">
                <GitBranch className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">No workflow runs yet</h3>
                <p className="text-muted-foreground mb-4">
                  Start a workflow to begin tracking progress.
                </p>
                <Button onClick={() => navigate(`/space/${projectId}/tasks`)}>
                  <Play className="w-4 h-4 mr-2" />
                  Go to Workflows
                </Button>
              </div>
            ) : filteredRuns.length === 0 ? (
              <div className="border rounded-lg p-8 text-center">
                <CheckCircle2 className="w-10 h-10 mx-auto text-green-500 mb-3" />
                <h3 className="text-lg font-medium mb-2">All runs completed</h3>
                <p className="text-muted-foreground mb-4">
                  Toggle "Show completed" above to view completed runs.
                </p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden bg-card shadow-sm">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Workflow</TableHead>
                      <TableHead>Run Name</TableHead>
                      <TableHead>Started By</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Activity</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRuns.map((run) => {
                      // O(1) lookup from pre-computed metrics
                      const { progress, dueDate, isOverdue } = getRunMetrics(run.id);

                      return (
                        <TableRow
                          key={run.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/space/${projectId}/workflow-run/${run.id}`)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0 bg-gradient-to-br from-blue-500 to-purple-600"
                              />
                              <span className="font-medium text-sm truncate max-w-[150px]">
                                {run.workflow?.name || 'Unknown Workflow'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">
                            {run.run_name || 'Untitled Run'}
                          </TableCell>
                          <TableCell>
                            {run.trigger_type === 'webhook' || (!run.started_by && !run.started_by_user) ? (
                              <div className="flex items-center gap-2">
                                <div className="h-6 w-6 rounded-full bg-violet-100 flex items-center justify-center">
                                  <Webhook className="h-3.5 w-3.5 text-violet-600" />
                                </div>
                                <span className="text-sm">Webhook</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Avatar className="h-6 w-6">
                                  <AvatarImage src={run.started_by_user?.avatar_url} />
                                  <AvatarFallback className="text-xs">
                                    {run.started_by_user?.first_name?.charAt(0) || '?'}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-sm">{run.started_by_user ? `${run.started_by_user.first_name || ''} ${run.started_by_user.last_name || ''}`.trim() : 'Unknown'}</span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {run.started_at
                              ? formatDistanceToNow(new Date(run.started_at), { addSuffix: true })
                              : '-'
                            }
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {run.updated_date
                              ? formatDistanceToNow(new Date(run.updated_date), { addSuffix: true })
                              : '-'
                            }
                          </TableCell>
                          <TableCell>
                            {dueDate ? (
                              <div className={`flex items-center gap-1 text-sm ${isOverdue ? 'text-red-600' : 'text-muted-foreground'}`}>
                                <Calendar className="w-3.5 h-3.5" />
                                <span>{format(dueDate, 'MMM d, yyyy')}</span>
                                {isOverdue && <span className="text-xs font-medium">(Overdue)</span>}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(run.status)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 min-w-[120px]">
                              <Progress value={progress.percent} className="h-2 flex-1" />
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {progress.completed}/{progress.total}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/space/${projectId}/workflow-run/${run.id}`);
                                }}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  View Run
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/space/${projectId}/library/${run.workflow_id}`);
                                }}>
                                  <GitBranch className="w-4 h-4 mr-2" />
                                  View Workflow
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteRunId(run.id);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete Run
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
        {/* Delete Run Dialog */}
        <AlertDialog open={!!deleteRunId} onOpenChange={(open) => !open && setDeleteRunId(null)}>
          <AlertDialogContent className="p-0 overflow-hidden">
            <div className="p-6 space-y-4">
              <div>
                <AlertDialogTitle>Delete Run</AlertDialogTitle>
                <AlertDialogDescription className="mt-2">
                  Are you sure you want to delete this run? This action cannot be undone.
                </AlertDialogDescription>
              </div>
              <AlertDialogFooter className="gap-3 pt-2">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                  onClick={async () => {
                    try {
                      await supabase
                        .from('checklists_workflow_runs')
                        .delete()
                        .eq('id', deleteRunId);
                      setWorkflowRuns(prev => prev.filter(r => r.id !== deleteRunId));
                      toast.success('Run deleted');
                    } catch (err) {
                      console.error('Error deleting run:', err);
                      toast.error('Failed to delete run');
                    }
                    setDeleteRunId(null);
                  }}
                >
                  Delete Run
                </AlertDialogAction>
              </AlertDialogFooter>
            </div>
          </AlertDialogContent>
        </AlertDialog>
      </PageTransition>
    </AppShell3>
  );
}
